import type { OrderDetail } from "../../services/use-orders"
import type { ExchangeRates } from "../../lib/money"
import { Money } from "../ui/money"
import { CopyValue } from "../ui/copy-value"
import { cn } from "../../lib/utils"

export function OrderItems({
  data,
  rates,
}: {
  data: OrderDetail
  rates: ExchangeRates | undefined
}) {
  const totals = [
    { label: "Items", amount: data.salesRevenueAmount },
    { label: "Shipping", amount: data.shippingPrice },
    ...(data.commissionTotal === null
      ? []
      : [{ label: "Marketplace commission", amount: -Math.abs(data.commissionTotal) }]),
    { label: "Refunded", amount: data.refundAmount },
    { label: "Order total", amount: data.totalPrice },
  ]
  return (
    <section aria-label="Items" className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <header className="flex items-center justify-between border-b border-border pb-4">
        <h2 className="text-sm font-semibold">Items</h2>

        <span className="text-xs text-muted-foreground">{data.lineCount} lines</span>
      </header>

      {data.lines.length === 0 ? (
        <p className="py-6 text-sm text-muted-foreground">No line items.</p>
      ) : (
        <ol className="divide-y divide-border">
          {data.lines.map((line, index) => (
            <li
              key={`${line.sku}:${index}`}
              className="grid grid-cols-[40px_minmax(0,1fr)] items-start gap-3 py-4 sm:grid-cols-[40px_minmax(0,1fr)_auto]"
            >
              <span className="flex size-10 items-center justify-center rounded-lg bg-muted text-sm font-semibold">
                {line.qty}×
              </span>

              <div className="min-w-0">
                <p className="break-words text-sm font-medium">{line.name ?? "Unnamed item"}</p>

                <div className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                  <CopyValue value={line.sku} className="font-mono" />

                  <span>
                    <Money amount={line.unitPrice} currency={data.currency} rates={rates} /> each
                  </span>
                </div>
              </div>

              <Money
                amount={line.total}
                currency={data.currency}
                rates={rates}
                className="col-start-2 text-sm font-semibold sm:col-start-3"
              />
            </li>
          ))}
        </ol>
      )}

      <dl className="ml-auto max-w-[280px] space-y-3 border-t border-border pt-4">
        {totals.map((total) => (
          <div
            key={total.label}
            className={cn(
              "flex items-baseline justify-between gap-4 text-sm",
              total.label === "Order total" && "border-t border-border pt-3 font-semibold"
            )}
          >
            <dt className={total.label === "Order total" ? undefined : "text-muted-foreground"}>
              {total.label}
            </dt>

            <dd
              className={
                total.label === "Refunded" && total.amount > 0 ? "text-warning" : undefined
              }
            >
              <Money amount={total.amount} currency={data.currency} rates={rates} />
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
